import { RULE_LOGIC, RULE_OPERATORS, type RuleOperator, type TriggerRule } from './rules.ts'

/**
 * The editable form of a rule — a flat list of `path · operator · value` rows —
 * and the two translations between it and the rule as stored.
 *
 * **It lives in core because it is a rule, not a rendering.** The console has no
 * test runner, and an absent runner is a fact about that workspace rather than a
 * licence for the code placed in it: this pair had a defect that every gate
 * accepted (see below), and it survived precisely because nothing could execute
 * it. `editor.ts` states the same principle for the canvas's placement rules.
 *
 * ## The stored shape, and why flattening is not symmetric with nesting
 *
 * `normaliseRule` reads a non-operator key over an object of non-operators as a
 * **scope**, so the v1 preset is stored three levels deep:
 *
 * ```json
 * { "$sale": { "openCardsIn": { "pl_delivery": { "_eq": 0 } } } }
 * ```
 *
 * A row is flat, so reading joins those keys with `.` and writing has to split
 * them apart again. **Writing the dotted path back as a single literal key is
 * grammatical and wrong**, and wrong in the worst available way: `normaliseRule`
 * accepts it, `rulePathsIn` reports it, `validateFlowGraph` passes it — and
 * `evaluateRule` then looks for a field literally named
 * `"$sale.openCardsIn.pl_delivery"`, does not find it, and returns `false` under
 * the *missing field counts as false* rule. The condition takes its `reject`
 * edge for ever, with `ok: true`, no error step and no failed run. That is the
 * defect this module was moved and rewritten to fix, and the round-trip test
 * below is what stops it coming back.
 *
 * **Dots are the path separator**, which is the assumption `rulePathsIn` and
 * `checkPath` already make: a key containing a literal dot cannot be expressed
 * as a row, and `ruleToRows` returns `null` for such a rule rather than
 * silently splitting it. **An empty key is refused for the same reason and in
 * the same direction**: a row with no path is dropped by `rowsToRule`'s filter,
 * so reading `{"": {"_eq": 1}}` into a row and writing it back would yield `{}`
 * — a clause that tested something becoming a rule that tests nothing and
 * therefore always passes. That is the permissive twin of the defect this
 * module was rewritten for, and it is refused rather than round-tripped.
 */

const LIST_OPERATORS: readonly RuleOperator[] = ['_in', '_nin']
const BOOLEAN_OPERATORS: readonly RuleOperator[] = ['_null', '_nnull']

/** One editable line of a rule. `value` is raw text as typed. */
export interface RuleRow {
  path: string
  operator: RuleOperator
  value: string
}

/** `_in` / `_nin` take a list of operands, entered comma-separated. */
export const takesList = (op: RuleOperator): boolean => LIST_OPERATORS.includes(op)

/** `_null` / `_nnull` take a boolean, so a value box would mean nothing. */
export const takesBoolean = (op: RuleOperator): boolean => BOOLEAN_OPERATORS.includes(op)

const isOperator = (key: string): key is RuleOperator =>
  (RULE_OPERATORS as readonly string[]).includes(key)

const isLogic = (key: string): boolean => (RULE_LOGIC as readonly string[]).includes(key)

/** A scalar as it should be stored: numbers and booleans keep their type. */
function scalar(raw: string): string | number | boolean | null {
  const trimmed = raw.trim()
  if (trimmed === '') return ''
  if (trimmed === 'true') return true
  if (trimmed === 'false') return false
  if (trimmed === 'null') return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : trimmed
}

/** The operand a row's operator expects, from its raw text. */
function operandOf(row: RuleRow): unknown {
  if (takesBoolean(row.operator)) return row.value.trim() !== 'false'
  if (takesList(row.operator)) {
    return row.value
      .split(',')
      .map((v) => scalar(v))
      .filter((v) => v !== '')
  }
  return scalar(row.value)
}

/**
 * `a.b.c` + a leaf → `{ a: { b: { c: <leaf> } } }`.
 *
 * The whole point of this module: a dotted path is a *route through scopes*, and
 * only its last segment carries the operator.
 */
function nest(path: string, leaf: Record<string, unknown>): TriggerRule {
  const segments = path.split('.').filter((s) => s !== '')
  let built: Record<string, unknown> = leaf
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    built = { [segments[i]!]: built }
  }
  return built as TriggerRule
}

/**
 * Rows → the stored rule. One row is written bare; several are wrapped in
 * `_and`, which is the canonical form `normaliseRule` would produce anyway.
 */
export function rowsToRule(rows: readonly RuleRow[]): TriggerRule {
  const clauses = rows
    .filter((r) => r.path.trim() !== '')
    .map((r) => nest(r.path.trim(), { [r.operator]: operandOf(r) }))

  if (clauses.length === 0) return {} as TriggerRule
  if (clauses.length === 1) return clauses[0]!
  return { _and: clauses } as TriggerRule
}

/** The stored operand as the text a row shows for it. */
function textOf(operand: unknown): string {
  if (Array.isArray(operand)) return operand.map((v) => String(v)).join(', ')
  return String(operand)
}

/**
 * The stored rule → rows, or `null` when it is a shape a flat list cannot hold.
 *
 * `null` is the honest answer rather than a partial reading: the caller shows
 * the raw JSON instead, so a rule written by a newer console — or by hand —
 * survives being looked at. `_or`, several operators on one leaf, the scalar
 * shorthand, and an empty or dotted key all return `null` for that reason.
 *
 * **A nested `_and` is flattened, not refused**, and that is deliberate:
 * `_and[_and[x]] ≡ x`, so the rows it yields write back to a rule that means
 * the same thing. An earlier version of this comment claimed it returned
 * `null`; the code was right and the prose was wrong, which is its own kind of
 * defect — a reader who trusted it would have added a redundant guard.
 */
export function ruleToRows(rule: unknown): RuleRow[] | null {
  if (rule === null || typeof rule !== 'object' || Array.isArray(rule)) return null
  const entries = Object.entries(rule as Record<string, unknown>)
  if (entries.length === 0) return []

  // The one logic form a flat list can hold: a top-level `_and` of rows.
  if (entries.length === 1 && entries[0]![0] === '_and') {
    const branches = entries[0]![1]
    if (!Array.isArray(branches)) return null
    const rows: RuleRow[] = []
    for (const branch of branches) {
      const parsed = ruleToRows(branch)
      if (parsed === null) return null
      rows.push(...parsed)
    }
    return rows
  }

  const rows: RuleRow[] = []
  for (const [key, value] of entries) {
    if (isLogic(key) || isOperator(key)) return null
    // Two keys a row cannot carry, both refused rather than half-read: one that
    // already contains the separator, and the empty one — `rowsToRule` drops a
    // pathless row, so an empty key would round-trip a real clause into `{}`,
    // which passes unconditionally.
    if (key === '' || key.includes('.')) return null
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return null

    const inner = Object.entries(value as Record<string, unknown>)
    if (inner.length !== 1) return null
    const [innerKey, operand] = inner[0]!

    if (isOperator(innerKey)) {
      rows.push({ path: key, operator: innerKey, value: textOf(operand) })
      continue
    }

    // A scope: recurse, and prefix every row it yields with this key.
    const nested = ruleToRows(value)
    if (nested === null) return null
    rows.push(...nested.map((r) => ({ ...r, path: `${key}.${r.path}` })))
  }
  return rows
}

/**
 * The v1 preset the plan names: *no open card in {target} on this sale*.
 *
 * A row rather than a rule, so it arrives in the builder as something a person
 * can then edit — and so it travels the same `rowsToRule` the rest of the list
 * does, instead of being a second way to write the same condition.
 */
export const noOpenCardPreset = (targetPipelineId: string): RuleRow => ({
  path: `$sale.openCardsIn.${targetPipelineId}`,
  operator: '_eq',
  value: '0',
})
